import { ClientError, GraphQLClient } from 'graphql-request';
import { Observable, defer, firstValueFrom, take, timeout, zip } from 'rxjs';
import { environment } from '../environment';
import { authService } from '../shared/services/auth/auth.service';

const appEnv = import.meta.env.VITE_REACT_APP_ENV as keyof typeof environment;
const client = new GraphQLClient(environment[appEnv].api);
const REFRESH_ID_TOKEN_TIMEOUT_MS = 15_000;

/**
 * In-flight Cognito refresh deduplication.
 *
 * When the user opens a page that fires N parallel dictionary queries on
 * mount (a typical create-order screen does ~5–10), an expired idToken
 * would otherwise trigger N independent `fetchAuthSession({ forceRefresh })`
 * calls, hammering Cognito and risking rate-limit throttles. Holding a
 * single Promise that all callers await collapses those into one network
 * round-trip; once it resolves the slot is cleared so the next legitimate
 * refresh can run on its own.
 */
let refreshInFlight: Promise<string> | null = null;
function refreshIdTokenOnce(): Promise<string> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = firstValueFrom(
    authService.sub.refreshIdToken().pipe(timeout({ first: REFRESH_ID_TOKEN_TIMEOUT_MS })),
  ).finally(() => {
    // Clear on a microtask so any callers that started reading immediately
    // after the awaited refresh land on a fresh slot — not on the same
    // resolved Promise that's no longer relevant.
    queueMicrotask(() => {
      refreshInFlight = null;
    });
  });
  return refreshInFlight;
}

function is401(err: unknown): boolean {
  return err instanceof ClientError && err.response.status === 401;
}

function buildHeaders(token: string, alternativeGuid: string): Record<string, string> {
  return {
    Authorization: encodeURIComponent(token),
    ...(environment[appEnv].alternativeGuid && { alternativeguid: alternativeGuid || '' }),
  };
}

type ExpectedAuthError = { __sentryIgnore?: boolean; __sessionExpired?: boolean };

/**
 * Mark a genuine session-expiry error so neither Sentry path files a ticket.
 *
 * These errors reach Sentry through two transports, each needing its own flag:
 *   - `__sessionExpired` is read by the GraphQL `responseHandler`, which
 *     catches the error and reports it via `captureMessage` (a Sentry *Message*
 *     event). This is the path that actually produced the WA-3514…WA-3538
 *     ticket flood — a Message event has no `originalException`, so the
 *     `__sentryIgnore` flag below never reaches `beforeSend` for it.
 *   - `__sentryIgnore` is read by `beforeSend` in main.tsx and is the backup
 *     for the rarer path where the error escapes uncaught (captureException).
 *
 * Set ONLY for 401s that end the session and redirect to login. A 401 that
 * survives a fresh-token retry is a real backend/authorizer bug and must keep
 * reaching Sentry — see `gqlClient`.
 */
function markSessionExpired(err: unknown): void {
  if (err && typeof err === 'object') {
    (err as ExpectedAuthError).__sentryIgnore = true;
    (err as ExpectedAuthError).__sessionExpired = true;
  }
}

function expireSession(err: unknown): void {
  markSessionExpired(err);
  void authService.expireSession();
}

/**
 * Execute a GraphQL operation with automatic recovery from expired sessions.
 *
 * Flow:
 *   1. Fetch the current idToken (Amplify uses its in-memory cache; near-expiry
 *      tokens commonly resolve to `''`).
 *   2. If the token is empty, force-refresh BEFORE sending — avoids the
 *      guaranteed-401 round-trip of submitting `Authorization: ''`.
 *   3. Send the request. On a transport-level 401, force-refresh once and
 *      retry. If the refresh itself fails (refresh token dead / server-side
 *      logout), that's a genuine session end: mark the error, expire the local
 *      session, and redirect to login before rethrowing for the caller's error
 *      UX. But if a *freshly refreshed* token is still rejected with 401, a
 *      valid token is being refused — a real backend/authorizer bug — so we do
 *      NOT mark or redirect (a fresh login would loop straight back into the
 *      same rejection); the error flows through to Sentry as a real signal.
 *   4. Non-401 errors pass through untouched.
 *
 * See WA-3514 / WA-3515: previously a stale token would send `''` and the
 * resulting 401 fed Sentry, which auto-created Jira tickets for what is
 * really just an end-of-session lifecycle event.
 */
export const gqlClient = (gql: string, variables: Record<string, any> = {}): Observable<any> => {
  return defer(() =>
    zip(authService.sub.idToken(), authService.sub.customerGUID()).pipe(take(1)),
  ).pipe(
    take(1),
    // Use a Promise-returning function inside switchMap so the
    // refresh-and-retry control flow stays linear and readable.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (source$) =>
      new Observable<any>((subscriber) => {
        const sub = source$.subscribe({
          next: async ([initialToken, alternativeGuid]) => {
            try {
              const effectiveToken = initialToken || (await refreshIdTokenOnce());
              if (!effectiveToken) {
                // Cognito session is irrecoverable — surface a clean error
                // and skip Sentry; the auth flow will redirect to login.
                const err = new Error('Session expired') as Error & ExpectedAuthError;
                expireSession(err);
                subscriber.error(err);
                return;
              }

              try {
                const data = await client.request(
                  gql,
                  variables,
                  buildHeaders(effectiveToken, alternativeGuid),
                );
                subscriber.next(data);
                subscriber.complete();
                return;
              } catch (err) {
                if (!is401(err)) {
                  subscriber.error(err);
                  return;
                }
                // Single retry with a freshly refreshed token.
                const refreshed = await refreshIdTokenOnce();
                if (!refreshed) {
                  expireSession(err);
                  subscriber.error(err);
                  return;
                }
                try {
                  const data = await client.request(
                    gql,
                    variables,
                    buildHeaders(refreshed, alternativeGuid),
                  );
                  subscriber.next(data);
                  subscriber.complete();
                } catch (retryErr) {
                  // A freshly refreshed token was still rejected with 401: a
                  // valid token is being refused, which is a backend/authorizer
                  // bug, not an expired session. Do NOT mark it expected and do
                  // NOT redirect (re-login would hit the same rejection and
                  // loop) — let it reach Sentry as the "something is really
                  // wrong" signal.
                  subscriber.error(retryErr);
                }
              }
            } catch (outerErr) {
              subscriber.error(outerErr);
            }
          },
          error: (err) => subscriber.error(err),
        });
        return () => sub.unsubscribe();
      }),
  );
};
